import { useOnboardingStore } from './store';

/**
 * Dictionary ID/EN. Port dari design-reference/components/i18n.jsx +
 * key tambahan (password, KTP photo, dll).
 */
type Dict = Record<string, string>;

export const I18N: Record<'id' | 'en', Dict> = {
    id: {
        // App
        appName: 'Peruri e-KYC',
        appTagline: 'Registrasi & Verifikasi Digital',

        // Welcome
        welcomeTitle: 'Selamat Datang di\nPeruri e-KYC',
        welcomeSubtitle:
            'Proses verifikasi identitas digital yang aman, cepat, dan bersertifikat sesuai standar Dukcapil dan PSrE.',
        getStarted: 'Mulai Registrasi',
        haveAccount: 'Sudah memiliki akun? Masuk',

        // Prepare
        prepareTitle: 'Persiapkan sebelum memulai',
        prepareKtp: 'KTP Elektronik yang masih berlaku',
        prepareKtpSub: 'Pastikan foto KTP jelas dan tidak buram',
        prepareLight: 'Ruangan dengan pencahayaan cukup',
        prepareLightSub: 'Diperlukan untuk proses liveness detection',
        prepareDevice: 'Perangkat dengan kamera depan',
        prepareDeviceSub: 'Untuk verifikasi wajah secara langsung',
        prepareTime: 'Waktu sekitar 5 menit',
        prepareTimeSub: 'Proses dapat dilanjutkan kapan saja',
        agreeTerms:
            'Saya menyetujui Syarat & Ketentuan serta Kebijakan Privasi Peruri',
        continue: 'Lanjutkan',

        // User type
        userTypeTitle: 'Jenis Akun',
        userTypeSubtitle: 'Pilih jenis akun yang sesuai dengan kebutuhan Anda',
        userIndividu: 'Individu',
        userIndividuDesc: 'Registrasi untuk perorangan dengan KTP pribadi',
        userCorporate: 'Badan Usaha',
        userCorporateDesc: 'Registrasi untuk perusahaan atau lembaga',
        corporateDisabled:
            'Flow Badan Usaha belum tersedia pada versi ini. Gunakan Individu.',

        // Phone
        phoneTitle: 'Masukkan Nomor Handphone',
        phoneSubtitle: 'Kami akan mengirimkan kode verifikasi melalui SMS',
        phoneLabel: 'Nomor Handphone',
        phoneHint: 'Pastikan nomor aktif untuk menerima SMS OTP',
        sendOtp: 'Kirim Kode OTP',

        // OTP
        otpTitle: 'Masukkan Kode Verifikasi',
        otpSubtitle: 'Kami telah mengirim kode 6 digit ke',
        otpResend: 'Kirim ulang kode',
        otpResendIn: 'Kirim ulang dalam',
        otpWrongNumber: 'Ganti nomor handphone',
        verify: 'Verifikasi',
        otpDevHint: 'Mode dev — OTP:',

        // Data Form
        dataTitle: 'Data Pribadi',
        dataSubtitle: 'Lengkapi data sesuai dengan KTP Anda',
        labelNik: 'Nomor Induk Kependudukan (NIK)',
        labelName: 'Nama Lengkap (sesuai KTP)',
        labelBirthPlace: 'Tempat Lahir',
        labelBirthDate: 'Tanggal Lahir',
        labelGender: 'Jenis Kelamin',
        labelAddress: 'Alamat (sesuai KTP)',
        labelCity: 'Kota',
        labelProvince: 'Provinsi',
        labelEmail: 'Alamat Email',
        labelPassword: 'Password Akun',
        labelPasswordConfirm: 'Konfirmasi Password',
        passwordHint: 'Minimal 8 karakter',
        labelKtpPhoto: 'Foto KTP',
        ktpPhotoHint: 'Ambil foto / pilih dari galeri. Maks 5MB.',
        labelNpwp: 'NPWP (opsional)',
        labelNpwpPhoto: 'Foto NPWP (opsional)',
        genderMale: 'Laki-laki',
        genderFemale: 'Perempuan',

        // Liveness
        livenessTitle: 'Verifikasi Wajah',
        livenessSubtitle: 'Ikuti instruksi berikut untuk memastikan identitas Anda',
        livenessReady: 'Posisikan wajah di dalam frame',
        livenessLookCenter: 'Lihat lurus ke kamera',
        livenessBlink: 'Kedipkan mata Anda',
        livenessTurnRight: 'Putar wajah ke kanan',
        livenessTurnLeft: 'Putar wajah ke kiri',
        livenessSmile: 'Berikan senyuman',
        livenessProcessing: 'Memproses verifikasi wajah',
        livenessStart: 'Mulai Verifikasi',
        livenessRetry: 'Coba Lagi',
        livenessAllowCamera: 'Berikan izin kamera & mikrofon untuk melanjutkan.',

        // Signature
        signTitle: 'Tanda Tangan Digital',
        signSubtitle: 'Goreskan tanda tangan Anda di area yang tersedia',
        signHint: 'Tanda tangan ini akan digunakan pada dokumen digital Anda',
        signClear: 'Hapus',
        signConfirm: 'Konfirmasi Tanda Tangan',
        signDrawHere: 'Goreskan di sini',

        // Review
        reviewTitle: 'Tinjau Data Anda',
        reviewSubtitle: 'Pastikan semua informasi sudah benar sebelum dikirim',
        reviewEdit: 'Ubah',
        reviewSubmit: 'Kirim & Selesaikan',
        reviewStatusTitle: 'Status Verifikasi',
        reviewStatusRegistration: 'Registrasi Peruri',
        reviewStatusPhone: 'Nomor Handphone',
        reviewStatusLiveness: 'Liveness Detection',
        reviewStatusSignature: 'Tanda Tangan Digital',

        // Success
        successTitle: 'Registrasi Berhasil',
        successSubtitle: 'Akun Anda telah terverifikasi dan siap digunakan',
        successId: 'Nomor Registrasi',
        successDownload: 'Unduh Sertifikat',
        successDashboard: 'Ke Beranda',
        successAutoLoginNote:
            'Anda sudah login otomatis. Silakan lanjutkan ke dashboard.',

        // Common
        back: 'Kembali',
        stepOf: 'Langkah {{current}} / {{total}}',
        retry: 'Coba Lagi',
        somethingWrong: 'Terjadi kesalahan. Silakan coba lagi.',
    },

    en: {
        appName: 'Peruri e-KYC',
        appTagline: 'Digital Registration & Verification',
        welcomeTitle: 'Welcome to\nPeruri e-KYC',
        welcomeSubtitle:
            'Secure, fast, and certified digital identity verification compliant with Dukcapil and PSrE standards.',
        getStarted: 'Start Registration',
        haveAccount: 'Already have an account? Sign in',

        prepareTitle: 'Prepare before you begin',
        prepareKtp: 'Valid Electronic ID Card (KTP)',
        prepareKtpSub: 'Make sure the KTP photo is clear and not blurry',
        prepareLight: 'A well-lit room',
        prepareLightSub: 'Required for the liveness detection process',
        prepareDevice: 'A device with a front camera',
        prepareDeviceSub: 'To verify your face in real time',
        prepareTime: 'Around 5 minutes',
        prepareTimeSub: 'You can resume the process anytime',
        agreeTerms:
            'I agree to the Terms of Service and Privacy Policy of Peruri',
        continue: 'Continue',

        userTypeTitle: 'Account Type',
        userTypeSubtitle: 'Choose the type of account that fits your needs',
        userIndividu: 'Individual',
        userIndividuDesc: 'Registration for individuals with personal KTP',
        userCorporate: 'Corporate',
        userCorporateDesc: 'Registration for companies or institutions',
        corporateDisabled:
            'Corporate flow is not available in this version. Please choose Individual.',

        phoneTitle: 'Enter your phone number',
        phoneSubtitle: 'We will send a verification code via SMS',
        phoneLabel: 'Phone Number',
        phoneHint: 'Make sure the number is active to receive the OTP SMS',
        sendOtp: 'Send OTP',

        otpTitle: 'Enter verification code',
        otpSubtitle: 'We sent a 6-digit code to',
        otpResend: 'Resend code',
        otpResendIn: 'Resend in',
        otpWrongNumber: 'Change phone number',
        verify: 'Verify',
        otpDevHint: 'Dev mode — OTP:',

        dataTitle: 'Personal Information',
        dataSubtitle: 'Fill in the data according to your KTP',
        labelNik: 'National Identity Number (NIK)',
        labelName: 'Full Name (as on KTP)',
        labelBirthPlace: 'Place of Birth',
        labelBirthDate: 'Date of Birth',
        labelGender: 'Gender',
        labelAddress: 'Address (as on KTP)',
        labelCity: 'City',
        labelProvince: 'Province',
        labelEmail: 'Email Address',
        labelPassword: 'Account Password',
        labelPasswordConfirm: 'Confirm Password',
        passwordHint: 'Minimum 8 characters',
        labelKtpPhoto: 'KTP Photo',
        ktpPhotoHint: 'Take photo / pick from gallery. Max 5MB.',
        labelNpwp: 'NPWP (optional)',
        labelNpwpPhoto: 'NPWP Photo (optional)',
        genderMale: 'Male',
        genderFemale: 'Female',

        livenessTitle: 'Face Verification',
        livenessSubtitle: 'Follow the instructions to confirm your identity',
        livenessReady: 'Position your face inside the frame',
        livenessLookCenter: 'Look directly at the camera',
        livenessBlink: 'Blink your eyes',
        livenessTurnRight: 'Turn your face right',
        livenessTurnLeft: 'Turn your face left',
        livenessSmile: 'Give a smile',
        livenessProcessing: 'Processing face verification',
        livenessStart: 'Start Verification',
        livenessRetry: 'Retry',
        livenessAllowCamera: 'Please allow camera and microphone to continue.',

        signTitle: 'Digital Signature',
        signSubtitle: 'Draw your signature in the provided area',
        signHint: 'This signature will be used on your digital documents',
        signClear: 'Clear',
        signConfirm: 'Confirm Signature',
        signDrawHere: 'Draw here',

        reviewTitle: 'Review Your Data',
        reviewSubtitle: 'Make sure all information is correct before submitting',
        reviewEdit: 'Edit',
        reviewSubmit: 'Submit & Finish',
        reviewStatusTitle: 'Verification Status',
        reviewStatusRegistration: 'Peruri Registration',
        reviewStatusPhone: 'Phone Number',
        reviewStatusLiveness: 'Liveness Detection',
        reviewStatusSignature: 'Digital Signature',

        successTitle: 'Registration Successful',
        successSubtitle: 'Your account has been verified and is ready to use',
        successId: 'Registration Number',
        successDownload: 'Download Certificate',
        successDashboard: 'Go to Dashboard',
        successAutoLoginNote:
            'You are already logged in. Please continue to the dashboard.',

        back: 'Back',
        stepOf: 'Step {{current}} / {{total}}',
        retry: 'Retry',
        somethingWrong: 'Something went wrong. Please try again.',
    },
};

export type Lang = keyof typeof I18N;
export type { Dict };

/**
 * Hook untuk ambil dictionary aktif.
 * Usage:
 *   const t = useT();
 *   <h1>{t.welcomeTitle}</h1>
 */
export function useT(): Dict {
    const lang = useOnboardingStore((s) => s.lang);
    return I18N[lang];
}

/**
 * Template helper untuk string dengan placeholder {{key}}.
 */
export function tpl(
    template: string,
    vars: Record<string, string | number>
): string {
    return template.replace(/\{\{(\w+)\}\}/g, (_, k) => String(vars[k] ?? ''));
}
